/**
 * Key Capture Button Component
 * Interactive button that captures keyboard input for key binding
 */

import { useState, useEffect, useRef } from 'react';
import { InputManager } from '../emulator/InputManager';

interface KeyCaptureButtonProps {
  currentKey: string | null;
  onKeyCapture: (key: string) => void;
  hasConflict?: boolean;
}

// Keys that should not be allowed for binding (system/browser shortcuts)
const FORBIDDEN_KEYS = new Set([
  'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
  'Tab', 'CapsLock', 'NumLock', 'ScrollLock',
  'ContextMenu', 'PrintScreen', 'Pause',
  'MetaLeft', 'MetaRight', 'OSLeft', 'OSRight',
]);

export default function KeyCaptureButton({
  currentKey,
  onKeyCapture,
  hasConflict = false,
}: KeyCaptureButtonProps) {
  const [isCapturing, setIsCapturing] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle key capture
  useEffect(() => {
    if (!isCapturing) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const code = event.code;

      // Allow Escape to cancel capture
      if (code === 'Escape') {
        setIsCapturing(false);
        return;
      }

      // Check if key is forbidden
      if (FORBIDDEN_KEYS.has(code)) {
        alert(`Cannot bind ${code}. This key is reserved for system use.`);
        setIsCapturing(false);
        return;
      }

      // Check if key is a modifier only (we want physical keys, not just modifiers)
      if (code.startsWith('Control') || code.startsWith('Alt') || code.startsWith('Meta')) {
        alert('Modifier keys alone cannot be bound. Try pressing a regular key.');
        return;
      }

      // Capture the key
      onKeyCapture(code);
      setIsCapturing(false);
    };

    const handleBlur = () => {
      setIsCapturing(false);
    };

    // Add event listeners
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('blur', handleBlur);

    // Focus the button to ensure key capture
    buttonRef.current?.focus();

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('blur', handleBlur);
    };
  }, [isCapturing, onKeyCapture]);

  // Start capturing
  const startCapture = () => {
    setIsCapturing(true);
  };

  // Get display name for the key
  const getKeyDisplayName = (code: string | null): string => {
    if (!code) {
      return 'Not Set';
    }
    return InputManager.getKeyName(code);
  };

  return (
    <button
      ref={buttonRef}
      className={`key-capture-button ${isCapturing ? 'capturing' : ''} ${hasConflict ? 'conflict' : ''}`}
      onClick={startCapture}
      disabled={isCapturing}
      aria-label={isCapturing ? 'Press a key to bind' : `Currently bound to ${getKeyDisplayName(currentKey)}`}
    >
      {isCapturing ? (
        <span className="capturing-text">Press a key...</span>
      ) : (
        <span className={`key-display ${currentKey ? '' : 'unset'}`}>
          {getKeyDisplayName(currentKey)}
        </span>
      )}
    </button>
  );
}
